// src/components/LoginPage.js
import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';

const LoginPage = () => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Preserve query (e.g. ?share=) so after login we can redirect back to it
      const query = window.location.search;
      if (query) {
        sessionStorage.setItem('auth_return_query', query);
      }

      // If the backend redirected the user here from a protected URL
      // (e.g. /api/websites/...), it appends ?next=<original-path>. Preserve
      // it across the SSO round-trip and validate it as a same-origin path
      // (no protocol-relative `//evil.com`, no absolute URLs) to avoid
      // becoming an open redirect.
      const params = new URLSearchParams(query);
      const nextParam = params.get('next');
      if (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') && !nextParam.startsWith('/\\')) {
        sessionStorage.setItem('auth_return_next', nextParam);
      } else {
        sessionStorage.removeItem('auth_return_next');
      }

      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message);
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '1rem'
    }}>
      <div style={{
        padding: '3rem 2rem',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%'
      }}>
        {/* Logo/Header */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <img
            // Absolute -- a relative path resolves against the CURRENT
            // URL's path, and this page can now render at a nested path
            // like /project/<id>/<id> (e.g. a stale/expired session on a
            // bookmarked project link), not just "/" as before. Also proxy-
            // aware, matching App.js's own logo path selection, so the
            // logo still resolves when the app is served under /proxy/3000.
            src={window.location.pathname.includes('/proxy/') ? '/proxy/3000/KAI.png' : '/KAI.png'}
            alt="KAI"
            style={{
              height: '120px',
              width: 'auto',
              marginBottom: '1rem',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          />
        </div>

        {/* Main Content */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.5rem',
            fontWeight: '500'
          }}>
            Sign in to continue
          </h2>
          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            lineHeight: '1.5'
          }}>
            This application requires Azure Single Sign-On.
            <br />
            Please sign in with your KPF account to access KAI.
          </p>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              marginBottom: '1rem',
              fontSize: '0.9rem'
            }}>
              <strong>Authentication Error:</strong> {error}
            </div>
          )}

          {/* Login Button */}
          <button
            onClick={handleLogin}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '1rem 2rem',
              backgroundColor: isLoading ? '#ccc' : '#3e61ee',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(94, 114, 228, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseOver={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#4c63d2';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(94, 114, 228, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#5e72e4';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(94, 114, 228, 0.3)';
              }
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Signing in...
              </>
            ) : (
              <>
                {/* Windows/Microsoft Logo */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M0 3.449L9.75 2.1v9.451H0zm10.949-1.32L24 .5v10.95H10.949zM0 13.25h9.75v9.451L0 20.699zm10.949.699H24V24l-13.051-1.801z"/>
                </svg>
                Login with Azure AD
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div style={{
          paddingTop: '2rem',
          borderTop: '1px solid #eee',
          color: '#888',
          fontSize: '0.9rem'
        }}>
          <p style={{ margin: 0 }}>
            🔒 Secure authentication through Microsoft Azure AD
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
